import React from "react";
import { Jimp } from "jimp";

export const iif = <T>(constraint: boolean, tVal: T, fVal?: T) =>
{
    return constraint ? tVal : fVal;
};

export const injectHTML = (s: string, tagName: string = "span") =>
{
    return React.createElement(tagName, {
        dangerouslySetInnerHTML: { __html: s }
    });
};

export function createGuid(): string
{
    return crypto.randomUUID();
}

export function deepCompare<T extends Record<string, any>>(a: T, b: T, ignoredKeys: (keyof T)[]): boolean
{
    const _a = { ...a };
    const _b = { ...b };

    for (const key of ignoredKeys) {

        if (key in _a) delete _a[key];
        if (key in _b) delete _b[key];
    }
    return deepEqual(_a, _b);
}

export function deepEqual(a: unknown, b: unknown): boolean
{
    const jsonA = JSON.stringify(a);
    const jsonB = JSON.stringify(b);
    return jsonA === jsonB;
}


const BASE_TITLE = "newReact";

export const setTitle = (...s: string[]) =>
{
    s = s.length ? s : [BASE_TITLE];
    const title = s.filter(s => s).join(" - ");
    window.document.title = title;
};

export const MAX_DEVICE_NUMBER: number = 65000;

/**
 * Creates a handler for passing on to a promises .catch function to get meaningful debug info in case the Promise rejects (either from Exception or intentional rejection)
 * Example:
 *
 *
 * @param fileName
 * @param locationInfo
 * @returns a handler that can be passed on tho promise.catch
 */
export function floatingPromiseHandler(
    fileName: string,
    functionName: string = "TODO: add fn.name in call site",
    locationInfo: string = `TODO: To find the exact location of this error caused by a floating promise please provide a locationInfo for all calls of ${floatingPromiseHandler.name} in ${fileName}`)
{
    return ((reasonForRejection: any) =>
    {
        console.error("Error caused by floating promise in:", fileName);
        console.error("In function:", functionName);
        console.error("At location:", locationInfo);
        console.error("reasonForRejection:", reasonForRejection);
    });
}


// JIMP image maniputation functions

// public interface

export type BaseNormalizeImageOptions = {
    maxWidth?: number, // when given: if image has greater height it gets scaled down to this
    maxHeight?: number, // when given: if image has greater width it gets scaled down to this
    debugLogImageInfo?: boolean; // just for debugging
};

export type NormalizeImageOptions =
    & BaseNormalizeImageOptions
    & (
        | {
            targetFormat: "image/jpeg";
            // quality: JPEG compression quality
            // Range: 0–100
            // 0: smallest file, very heavy artifacts
            // ~80: typical default / good visual quality
            // 100: best quality, largest file (still lossy, not lossless)
            quality: number;
        }
        | {
            targetFormat: "image/png";
            // compressionLevel: PNG zlib compression effort (lossless)
            // Range: 0–9
            // 0: no compression (fastest, largest file)
            // 6: typical default
            // 9: maximum compression (slowest, smallest file)
            compressionLevel: number;
        }
        // // gif export takes ages (because of palette i guess) and crashes the browser for large images
        // | {
        //     targetFormat: "image/gif";
        //     // no gif export options available
        // }
    );


/**
 * takes a file (from SettingsDropFile for example) and returns the base64 encoded dataUrl of the normalized version of the image
 * allows optional size limiting and jpeg and png output format with quality (for jpeg) and compressionLevel (for png)
 * supported input formats are: bmp, gif, jpeg, png and tiff according to according to the jimp documentation
 * at https://jimp-dev.github.io/jimp/guides/getting-started/
 * @param file
 * @param options
 * @returns dataUrl or undefined
 */
export async function normalizeImageFile(file: File, options: NormalizeImageOptions): Promise<string | undefined>
{
    //   const input = e.target as HTMLInputElement;
    //   const file = input.files?.[0];
    if (!file) return;

    const data = await file.arrayBuffer();

    const image: JimpImage = await Jimp.fromBuffer(data);

    if (options.debugLogImageInfo) {
        logJimpImageInfo(image, "image before normalization");
    }

    limitImageSize(image, options);

    if (options.debugLogImageInfo) {
        logJimpImageInfo(image, "image after normalization");
    }

    const jpegBytes = await image.getBuffer(options.targetFormat, options as any);
    const base64 = uint8ToBase64(jpegBytes instanceof Uint8Array ? jpegBytes : new Uint8Array(jpegBytes as any));

    const dataUrl = `data:${options.targetFormat};base64,${base64}`;

    return dataUrl;
}


// private implementation details

type JimpImage = Awaited<ReturnType<typeof Jimp.fromBuffer>>;

async function limitImageSize(
    image: JimpImage,
    opts: BaseNormalizeImageOptions
): Promise<JimpImage>
{
    const { maxWidth, maxHeight } = opts;

    if (!maxWidth && !maxHeight) return image;

    const w = image.bitmap.width;
    const h = image.bitmap.height;

    let scale = 1;

    if (maxWidth && w > maxWidth) scale = Math.min(scale, maxWidth / w);
    if (maxHeight && h > maxHeight) scale = Math.min(scale, maxHeight / h);

    if (scale >= 1) return image;

    const newW = Math.round(w * scale);
    const newH = Math.round(h * scale);

    image.resize({ w: newW, h: newH }); // WARNING: mutates the argument!
    return image;
}

// helper functions (created with chatGPT support :)
function uint8ToBase64(u8: Uint8Array): string
{
    // Browser-safe base64 for arbitrary bytes
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < u8.length; i += chunkSize) {
        binary += String.fromCharCode(...u8.subarray(i, i + chunkSize));
    }
    return btoa(binary);
}

function logJimpImageInfo(img: JimpImage, label = "JimpImage"): void
{
    const anyImg = img as any; // for optional/internal props

    const info = {
        width: img.bitmap.width,
        height: img.bitmap.height,
        mime: anyImg.mime ?? "(unknown)",
        hasAlpha: detectAlpha(img),
        background: anyImg.background,
        formats: anyImg.formats?.map((f: any) => f?.mime ?? f?.name) ?? [],
        dataLength: img.bitmap.data?.length,
        bytesPerPixel: img.bitmap.data
            ? img.bitmap.data.length / (img.bitmap.width * img.bitmap.height)
            : undefined,
    };

    console.log(`[${label}]`, info);
}

function detectAlpha(img: JimpImage): boolean
{
    // Jimp stores RGBA → every 4th byte is alpha
    const data = img.bitmap.data;
    if (!data) return false;

    for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 255) return true;
    }
    return false;
}
